/**
 * Accessibility Utilities
 * 
 * Helpers for WCAG 2.1 AA compliance and better UX
 */

/**
 * Generate unique IDs for aria-describedby relationships
 */
export function generateAriaId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Keyboard event handlers for common patterns
 */
export const keyboardHandlers = {
  /**
   * Handle Enter/Space as click for custom interactive elements
   */
  activateOnEnterOrSpace: (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  },

  /**
   * Handle Escape key to close modals/popovers
   */
  closeOnEscape: (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      callback();
    }
  },

  /**
   * Arrow navigation for lists
   */
  arrowNavigation: (options: {
    onUp?: () => void;
    onDown?: () => void;
    onLeft?: () => void;
    onRight?: () => void;
  }) => (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        options.onUp?.();
        break;
      case 'ArrowDown':
        e.preventDefault();
        options.onDown?.();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        options.onLeft?.();
        break;
      case 'ArrowRight':
        e.preventDefault();
        options.onRight?.();
        break;
    }
  },
};

/**
 * Screen reader only text (visually hidden but accessible)
 */
export const srOnly = 'sr-only';

/**
 * Focus management utilities
 */
export const focusManagement = {
  /**
   * Trap focus within an element (for modals)
   */
  trapFocus: (containerRef: React.RefObject<HTMLElement>) => {
    if (!containerRef.current) return;

    const focusableElements = containerRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    containerRef.current.addEventListener('keydown', handleTabKey);

    return () => {
      containerRef.current?.removeEventListener('keydown', handleTabKey);
    };
  },

  /**
   * Focus first invalid field in a form
   */
  focusFirstInvalid: (formRef: React.RefObject<HTMLFormElement>) => {
    if (!formRef.current) return;

    const invalidField = formRef.current.querySelector<HTMLElement>(
      '[aria-invalid="true"], .error input, .error select, .error textarea'
    );

    invalidField?.focus();
  },

  /**
   * Restore focus to previous element (after closing modal)
   */
  restoreFocus: (previousActiveElement: Element | null) => {
    if (previousActiveElement instanceof HTMLElement) {
      previousActiveElement.focus();
    }
  },
};

/**
 * Announce to screen readers (live region)
 */
export function announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite') {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}

/**
 * Validate color contrast (WCAG AA = 4.5:1 for normal text, 3:1 for large)
 */
export function getLuminance(hex: string): number {
  const rgb = parseInt(hex.slice(1), 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;

  const [rLum, gLum, bLum] = [r, g, b].map((val) => {
    const normalized = val / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * rLum + 0.7152 * gLum + 0.0722 * bLum;
}

export function getContrastRatio(foreground: string, background: string): number {
  const l1 = getLuminance(foreground);
  const l2 = getLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function meetsWCAG_AA(foreground: string, background: string, isLargeText = false): boolean {
  const ratio = getContrastRatio(foreground, background);
  return isLargeText ? ratio >= 3 : ratio >= 4.5;
}

/**
 * Common ARIA patterns
 */
export const ariaPatterns = {
  /**
   * Props for loading button
   */
  loadingButton: (isLoading: boolean) => ({
    'aria-busy': isLoading,
    'aria-disabled': isLoading,
  }),

  /**
   * Props for required field
   */
  requiredField: {
    'aria-required': true,
    required: true,
  },

  /**
   * Props for invalid field
   */
  invalidField: (errorId: string) => ({
    'aria-invalid': true,
    'aria-describedby': errorId,
  }),

  /**
   * Props for expanded/collapsed sections
   */
  expandable: (isExpanded: boolean, controlsId: string) => ({
    'aria-expanded': isExpanded,
    'aria-controls': controlsId,
  }),

  /**
   * Props for selected item in list
   */
  selected: (isSelected: boolean) => ({
    'aria-selected': isSelected,
    'aria-current': isSelected ? ('true' as const) : undefined,
  }),
};

/**
 * Skip to main content link
 */
export function SkipToMain() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
    >
      Pular para o conteúdo principal
    </a>
  );
}
